import { useCallback, useState } from "react"

import { useToggle } from "react-use"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, MoreVertRoundedIcon } from "@l2r-front/l2r-icons"
import { Currency, Task, TaskType } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice, IconButton, L2RButton, Menu, MenuItem, TruncableText, Typography } from "@l2r-front/l2r-ui"
import { formatNumber, parseFormattedNumber } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

import * as Styled from "./RoadworkTask.styled"

type RoadworkTaskComponentProps = {
    currency: Currency
    id: string
    isDeletable?: boolean
    editionEnabled?: boolean
    onDelete: () => void
    onCommentToggled?: (_displayed: boolean) => void
    onEdit?: () => void
    task: Task
    taskType?: TaskType | null
}

export function RoadworkTaskComponent(props: RoadworkTaskComponentProps) {

    const {
        currency,
        editionEnabled = true,
        id,
        isDeletable,
        onCommentToggled,
        onDelete,
        onEdit,
        task,
        taskType,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const open = Boolean(anchorEl)
    const [commentDisplayed, toggleCommentDisplayed] = useToggle(false)

    const handleMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleCommentDisplayedToggle = useCallback(() => {
        const commentShouldDisplay = !commentDisplayed
        toggleCommentDisplayed(commentShouldDisplay)
        if (onCommentToggled) {
            onCommentToggled(commentShouldDisplay)
        }
    }, [onCommentToggled, toggleCommentDisplayed, commentDisplayed])

    const taskCost = `${formatNumber(parseFormattedNumber(task.cost), 2)} ${currency.symbol}`

    return <Styled.RoadworkTaskWrapper id={id}>
        {!isMobile && <Styled.RoadworkTaskInfo withButton={false}>
            <Styled.LeftWrapper>
                <Typography variant="Desktop & Mobile/Heading/H3">{taskType?.name}</Typography>
                {task.comment &&
                    <L2RButton id="task-comment-button-placeholder" variant="text" onClick={handleCommentDisplayedToggle}>
                        <Typography variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.roadworkTask.comment")}</Typography>
                    </L2RButton>}
            </Styled.LeftWrapper>
            <Styled.RightWrapper>
                <Styled.TextWrapper>
                    <Styled.Text variant="Desktop & Mobile/Body/Regular" isMobile={isMobile}>{taskCost}</Styled.Text>
                    <Styled.Quantity variant="Desktop & Mobile/Body/Regular">{formatNumber(parseFormattedNumber(task.quantity), 2)} {task.unit}</Styled.Quantity>
                </Styled.TextWrapper>
                <Styled.TaskImpactBadge impact={taskType?.impact} variant="outlined" />
            </Styled.RightWrapper>
            {editionEnabled && <Styled.Overlay id="task-overlay">
                <Styled.RoadworkTaskInfo withButton={false}>
                    <Styled.LeftWrapper>
                        <Typography variant="Desktop & Mobile/Heading/H3">{taskType?.name}</Typography>
                        {task.comment && <L2RButton id="task-comment-button" variant="text" onClick={handleCommentDisplayedToggle}>
                            <Typography variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.roadworkTask.comment")}</Typography>
                        </L2RButton>}
                    </Styled.LeftWrapper>
                    <Styled.RightWrapper>
                        <Styled.TextWrapper hidden={true}>
                            <Styled.Text variant="Desktop & Mobile/Body/Regular" isMobile={isMobile}>{taskCost}</Styled.Text>
                            <Styled.Quantity variant="Desktop & Mobile/Body/Regular">{task.quantity} {task.unit}</Styled.Quantity>
                        </Styled.TextWrapper>
                        <Styled.TaskImpactBadge impact={taskType?.impact} variant="outlined" hidden={true} />
                        <Styled.OverlayButtonsContainer>
                            {isDeletable && <IconButton id="task-delete-button" onClick={onDelete}>
                                <DeleteIcon color="primary" />
                            </IconButton>}
                            <L2RButton id="task-edit-button" onClick={() => onEdit?.()}>{t(I18N_NAMESPACE, "components.roadworkTask.edit")}</L2RButton>
                        </Styled.OverlayButtonsContainer>
                    </Styled.RightWrapper>
                </Styled.RoadworkTaskInfo>
            </Styled.Overlay>}
        </Styled.RoadworkTaskInfo>
        }
        {isMobile && <>
            <Typography variant="Desktop & Mobile/Heading/H3">{taskType?.name}</Typography>
            <Styled.RoadworkTaskInfo withButton={editionEnabled}>
                <Styled.LeftWrapper>
                    <Styled.TextWrapper>
                        <Styled.Text variant="Desktop & Mobile/Body/Regular" isMobile={isMobile}>{taskCost}</Styled.Text>
                        <Styled.Quantity variant="Desktop & Mobile/Body/Regular">{formatNumber(parseFormattedNumber(task.quantity), 2)} {task.unit}</Styled.Quantity>
                    </Styled.TextWrapper>
                </Styled.LeftWrapper>
                <Styled.RightWrapper>
                    <Styled.TextWrapper>
                        <Styled.TaskImpactBadge impact={taskType?.impact} variant="outlined" />
                    </Styled.TextWrapper>
                </Styled.RightWrapper>
                {
                    editionEnabled &&
                    <div>
                        <IconButton id="task-menu" onClick={handleMenuClick}>
                            <MoreVertRoundedIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: "center",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "center",
                                horizontal: "right",
                            }}
                            open={open}
                            onClose={handleMenuClose}
                        >
                            <MenuItem key={"task-menu-edit"} id={"task-menu-edit"} onClick={() => onEdit?.()}>
                                {t(I18N_NAMESPACE, "components.roadworkTask.edit")}
                            </MenuItem>
                            {isDeletable && <MenuItem key={"task-menu-delete"} id={"task-menu-delete"} onClick={onDelete}>
                                {t(I18N_NAMESPACE, "components.roadworkTask.delete")}
                            </MenuItem>}
                        </Menu>
                    </div>
                }
            </Styled.RoadworkTaskInfo >
            {
                task.comment && <L2RButton id="task-comment-button" variant="text" onClick={handleCommentDisplayedToggle}>
                    <Typography variant="Desktop & Mobile/Body/Small">{t(I18N_NAMESPACE, "components.roadworkTask.comment")}</Typography>
                </L2RButton>
            }
        </>
        }
        {
            commentDisplayed && <Styled.Comments id="task-comment-text">
                <TruncableText>
                    {task.comment ?? ""}
                </TruncableText>
            </Styled.Comments>
        }
    </Styled.RoadworkTaskWrapper >
}
